import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import firebase from '../../Firebase/Firebase';

const resolutionCache = new Map();

const decodeHtmlEntities = (value) => String(value || '')
  .replace(/&amp;/gi, '&')
  .replace(/&#38;/g, '&');

const normalizeSource = (source) => {
  if (!source) return '';
  if (Array.isArray(source)) return normalizeSource(source.find(Boolean));

  if (typeof source === 'object') {
    return normalizeSource(
      source.downloadURL
      || source.downloadUrl
      || source.url
      || source.src
      || source.path
      || source.fullPath
      || source.value,
    );
  }

  const text = decodeHtmlEntities(source).trim();
  if (!text) return '';

  // A few early records were exported/imported as JSON strings.
  if ((text.startsWith('[') && text.endsWith(']')) || (text.startsWith('{') && text.endsWith('}'))) {
    try {
      return normalizeSource(JSON.parse(text));
    } catch {
      // Keep the original value when it is not valid JSON.
    }
  }

  return text;
};

const storagePathFromUrl = (source) => {
  const normalized = normalizeSource(source);
  if (!normalized) return '';
  if (normalized.startsWith('gs://')) return normalized;
  if (!/^https?:\/\//i.test(normalized)) return normalized.startsWith('/') ? '' : normalized;

  try {
    const url = new URL(normalized);

    // Firebase REST download URL: /v0/b/<bucket>/o/<encoded path>
    const firebaseMatch = url.pathname.match(/\/o\/(.+)$/);
    if (firebaseMatch) return decodeURIComponent(firebaseMatch[1]);

    // Some Firebase exports store the object path in ?name=...
    const name = url.searchParams.get('name');
    if (name) return decodeURIComponent(name);

    // Google Cloud Storage URL: storage.googleapis.com/<bucket>/<path>
    if (url.hostname === 'storage.googleapis.com') {
      const [, , ...pathParts] = url.pathname.split('/');
      return decodeURIComponent(pathParts.join('/'));
    }

    // Virtual-hosted GCS URL: <bucket>.storage.googleapis.com/<path>
    if (url.hostname.endsWith('.storage.googleapis.com')) {
      return decodeURIComponent(url.pathname.replace(/^\//, ''));
    }
  } catch {
    return '';
  }

  return '';
};

const resolveFirebaseUrl = async (source, { force = false } = {}) => {
  const normalized = normalizeSource(source);
  if (!normalized) return '';

  if (!force && resolutionCache.has(normalized)) {
    return resolutionCache.get(normalized);
  }

  const path = storagePathFromUrl(normalized);
  if (!path) return '';

  const promise = (async () => {
    try {
      const reference = path.startsWith('gs://')
        ? firebase.storage().refFromURL(path)
        : firebase.storage().ref(path);
      return await reference.getDownloadURL();
    } catch (error) {
      console.warn('[FirebaseImage] Impossible de résoudre l’image Firebase.', {
        source: normalized,
        storagePath: path,
        code: error?.code,
        message: error?.message,
      });
      return '';
    }
  })();

  resolutionCache.set(normalized, promise);
  return promise;
};

const FirebaseImage = forwardRef(({
  src,
  fallbackSrc = '',
  onError,
  onLoad,
  onResolved,
  onUnavailable,
  ...props
}, ref) => {
  const normalized = useMemo(() => normalizeSource(src), [src]);
  const [currentSrc, setCurrentSrc] = useState(normalized || fallbackSrc);
  const attempts = useRef(0);

  useEffect(() => {
    attempts.current = 0;
    setCurrentSrc(normalized || fallbackSrc);
    if (!normalized) onUnavailable?.({ reason: 'missing-source', source: '' });
  }, [normalized, fallbackSrc]);

  const handleLoad = (event) => {
    if (currentSrc && currentSrc !== fallbackSrc) {
      onResolved?.(currentSrc);
    }
    onLoad?.(event);
  };

  const handleError = async (event) => {
    onError?.(event);

    if (normalized && attempts.current < 2) {
      attempts.current += 1;

      // First retry: resolve the object through the authenticated Storage SDK.
      // Second retry: clear the cache and resolve again, useful just after auth hydration.
      if (attempts.current === 2) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        resolutionCache.delete(normalized);
      }

      const refreshed = await resolveFirebaseUrl(normalized, { force: attempts.current === 2 });
      if (refreshed) {
        const separator = refreshed.includes('?') ? '&' : '?';
        const nextSource = refreshed === currentSrc
          ? `${refreshed}${separator}lpb_retry=${attempts.current}-${Date.now()}`
          : refreshed;
        setCurrentSrc(nextSource);
        return;
      }
    }

    console.warn('[FirebaseImage] Image indisponible. Le visuel affiché est un placeholder, pas une image Firestore.', {
      source: normalized,
    });
    onUnavailable?.({ reason: 'load-failed', source: normalized });

    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
    }
  };

  return (
    <img
      ref={ref}
      src={currentSrc || fallbackSrc}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
});

FirebaseImage.displayName = 'FirebaseImage';

export default FirebaseImage;
